import { motion } from "framer-motion";
import { Icon } from "@iconify/react";

const INTEGRATIONS = [
    {
        name: "PayYetu",
        icon: "mdi:wallet-outline",
        color: "blue",
        description:
            "Recebe pagamentos directamente na tua conta PayYetu. Confirmação automática — sem verificação manual.",
        features: [
            "Pagamentos instantâneos",
            "Confirmação automática",
            "Reconciliação integrada",
        ],
    },
    {
        name: "Escola Inteligente",
        icon: "mdi:school-outline",
        color: "blue",
        description:
            "Facture matrículas e propinas directamente do sistema escolar. Integração bidirecional entre gestão escolar e facturação.",
        features: [
            "Facturação de propinas",
            "Matrícula com factura",
            "Relatórios unificados",
        ],
    },
    {
        name: "LogiTransporte",
        icon: "mdi:truck-outline",
        color: "amber",
        description:
            "Gestão completa de frotas, motoristas e rotas. Controla os custos de transporte e logística integrados com a facturação.",
        features: [
            "Gestão de frotas",
            "Controlo de rotas",
            "Custos por viagem",
        ],
    },
];

const COLOR_MAP: Record<string, { bg: string; text: string; border: string; dot: string }> = {
    blue: {
        bg: "bg-blue-500/10",
        text: "text-blue-400",
        border: "border-blue-500/20",
        dot: "bg-blue-400",
    },
    amber: {
        bg: "bg-amber-500/10",
        text: "text-amber-400",
        border: "border-amber-500/20",
        dot: "bg-amber-400",
    },
};

export default function IntegrationsSection() {
    return (
        <section className="py-20 lg:py-28 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="h-px w-8 bg-blue-500" />
                        <span className="text-blue-600 text-sm font-medium tracking-wider uppercase">
                            Ecossistema
                        </span>
                        <div className="h-px w-8 bg-blue-500" />
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                        Integrado com a plataforma{" "}
                        <span className="text-blue-600">HongaYetu</span>
                    </h2>
                    <p className="text-gray-500 max-w-2xl mx-auto">
                        O Logigate faz parte do ecossistema HongaYetu. Conecte-se com outros
                        serviços da plataforma para uma gestão ainda mais completa.
                    </p>
                </motion.div>

                {/* Integration cards */}
                <div className="grid md:grid-cols-3 gap-8">
                    {INTEGRATIONS.map((item, index) => {
                        const colors = COLOR_MAP[item.color];
                        return (
                            <motion.div
                                key={item.name}
                                initial={{ opacity: 0, y: 25 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                className="group relative bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
                            >
                                {/* Icon */}
                                <div
                                    className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-4`}
                                >
                                    <Icon
                                        icon={item.icon}
                                        width={24}
                                        className={colors.text}
                                    />
                                </div>

                                {/* Name & description */}
                                <h3 className="text-lg font-bold text-gray-900 mb-2">
                                    {item.name}
                                </h3>
                                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                                    {item.description}
                                </p>

                                {/* Features */}
                                <ul className="space-y-2">
                                    {item.features.map((feat) => (
                                        <li
                                            key={feat}
                                            className="flex items-center gap-2 text-sm text-gray-600"
                                        >
                                            <div
                                                className={`w-1.5 h-1.5 rounded-full ${colors.dot} shrink-0`}
                                            />
                                            {feat}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Bottom connector visual */}
                <motion.div
                    className="mt-12 flex items-center justify-center gap-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <div className="h-px w-16 bg-linear-to-r from-transparent to-gray-300" />
                    <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
                        <Icon icon="mdi:link-variant" width={18} className="text-gray-400" />
                        <span className="text-xs text-gray-500 font-medium">
                            Tudo conectado num único ecossistema
                        </span>
                    </div>
                    <div className="h-px w-16 bg-linear-to-l from-transparent to-gray-300" />
                </motion.div>
            </div>
        </section>
    );
}
